import React, { useState } from 'react';
import { UserAccount } from '../../types/user';
import { X, User, Check, Users, UserMinus } from 'lucide-react';

interface BulkAssignmentModalProps {
  orderCount: number;
  workers: UserAccount[];
  isOpen: boolean;
  onClose: () => void;
  onAssign: (workerId: number) => void;
  onDeassign?: () => void;
  loading?: boolean;
  hasAssignedOrders?: boolean;
}

const BulkAssignmentModal: React.FC<BulkAssignmentModalProps> = ({
  orderCount,
  workers,
  isOpen,
  onClose,
  onAssign,
  onDeassign,
  loading = false,
  hasAssignedOrders = false
}) => {
  const [selectedWorkerId, setSelectedWorkerId] = useState<number | null>(null);
  const [action, setAction] = useState<'assign' | 'deassign'>('assign');

  if (!isOpen) return null;

  const handleAction = () => {
    if (action === 'deassign' && onDeassign) {
      onDeassign();
    } else if (action === 'assign' && selectedWorkerId) {
      onAssign(selectedWorkerId);
    }
  };

  const availableWorkers = workers.filter(worker => worker.role === 'worker' && worker.is_active);

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-semibold text-gray-900">
            Bulk Assign Orders
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="mb-6">
          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="flex items-center">
              <Users className="h-5 w-5 text-blue-500 mr-2" />
              <div>
                <h4 className="font-medium text-blue-900">Bulk Assignment</h4>
                <p className="text-sm text-blue-700">
                  Manage {orderCount} selected {orderCount === 1 ? 'order' : 'orders'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {hasAssignedOrders && (
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Action
            </label>
            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="radio"
                  value="assign"
                  checked={action === 'assign'}
                  onChange={(e) => setAction(e.target.value as 'assign')}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                />
                <span className="ml-2 text-sm text-gray-700">Assign/Reassign to worker</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  value="deassign"
                  checked={action === 'deassign'}
                  onChange={(e) => setAction(e.target.value as 'deassign')}
                  className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300"
                />
                <span className="ml-2 text-sm text-gray-700">Deassign from all workers</span>
              </label>
            </div>
          </div>
        )}

        {action === 'assign' && (
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Select Worker
            </label>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {availableWorkers.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">
                  No available workers found
                </p>
              ) : (
                availableWorkers.map(worker => (
                  <div
                    key={worker.id}
                    className={`border rounded-lg p-3 cursor-pointer transition-colors ${
                      selectedWorkerId === worker.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => setSelectedWorkerId(worker.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="h-8 w-8 bg-gray-300 rounded-full flex items-center justify-center">
                          <User className="h-4 w-4 text-gray-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {worker.full_name}
                          </p>
                          <p className="text-xs text-gray-500">@{worker.username}</p>                          {worker.last_login_at && (
                            <p className="text-xs text-gray-400">
                              Last active: {new Date(worker.last_login_at).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                      </div>
                      {selectedWorkerId === worker.id && (
                        <Check className="h-5 w-5 text-blue-500" />
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {action === 'deassign' && (
          <div className="mb-6">
            <div className="bg-orange-50 border border-orange-200 rounded-md p-4">
              <div className="flex items-center">
                <UserMinus className="h-5 w-5 text-orange-500 mr-2" />
                <div>
                  <h4 className="font-medium text-orange-900">Deassign Workers</h4>
                  <p className="text-sm text-orange-700 mt-1">
                    This will remove worker assignments from all selected orders and return them to pending status.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleAction}
            disabled={loading || (action === 'assign' && !selectedWorkerId)}
            className={`inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
              action === 'deassign' 
                ? 'bg-orange-600 hover:bg-orange-700 focus:ring-orange-500'
                : 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'
            }`}
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                {action === 'deassign' ? 'Deassigning...' : 'Assigning...'}
              </>
            ) : (
              action === 'deassign' 
                ? `Deassign ${orderCount} ${orderCount === 1 ? 'Order' : 'Orders'}`
                : `Assign ${orderCount} ${orderCount === 1 ? 'Order' : 'Orders'}`
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkAssignmentModal;
